import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { loginRedirect } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Sparkles, Camera, ArrowLeft, CheckCircle2, Loader2, IdCard, ScanLine } from "lucide-react";

// 5-field onboarding wizard: photo · KYC · mobile · email · profession → AI fills the rest.
export default function OnboardingAI() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [form, setForm] = useState({
    photo_base64: "", kyc_image_base64: "", visiting_card_base64: "",
    mobile: user?.mobile || "", email: user?.email || "", profession: "",
  });

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center">
        <h2 className="heading text-3xl font-extrabold mb-3">Please login</h2>
        <Button onClick={loginRedirect} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="onb-login">Login with Google</Button>
      </div>
    );
  }

  const read = (key) => async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) return toast.error("Image must be under 6 MB");
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, [key]: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.photo_base64 || !form.kyc_image_base64 || !form.mobile || !form.email || !form.profession) {
      return toast.error("Photo, KYC, mobile, email and profession are required");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding/ai-autofill", form);
      setExtracted(data.extracted);
      toast.success("Profile drafted from your documents");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Extraction failed");
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding/confirm", { extracted });
      await checkAuth();
      toast.success("Profile updated");
      navigate("/account");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (extracted) return <ReviewCard extracted={extracted} onChange={setExtracted} onConfirm={confirm} busy={busy} onBack={() => setExtracted(null)} />;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6" data-testid="onb-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="onb-back"><ArrowLeft size={16} className="mr-1" /> Back</Button>
        <div>
          <h1 className="heading text-2xl font-extrabold flex items-center gap-2"><Sparkles className="text-[#E05E36]" /> AI Auto-Fill Onboarding</h1>
          <div className="text-sm text-stone-600">Give us 5 things — we'll fill the rest.</div>
        </div>
      </div>

      <Card><CardContent className="p-4 space-y-4">
        <UploadTile label="1 · Your photo" icon={<Camera size={16} />} value={form.photo_base64} onFile={read("photo_base64")} testId="onb-photo" />
        <UploadTile label="2 · KYC ID (Aadhaar / Passport / DL)" icon={<IdCard size={16} />} value={form.kyc_image_base64} onFile={read("kyc_image_base64")} testId="onb-kyc" />
        <UploadTile label="3 · Visiting card (optional — accelerates business fields)" icon={<ScanLine size={16} />} value={form.visiting_card_base64} onFile={read("visiting_card_base64")} testId="onb-card" optional />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Mobile"><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="+91 98…" data-testid="onb-mobile" /></Field>
          <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="onb-email" /></Field>
          <Field label="Profession (free text)"><Input value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} placeholder="e.g. Pediatrician / Software Engineer / Florist" data-testid="onb-profession" /></Field>
        </div>
        <Button disabled={busy} onClick={submit} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] gap-2 w-full" data-testid="onb-submit">
          {busy ? <><Loader2 size={14} className="animate-spin" /> Extracting…</> : <><Sparkles size={14} /> Auto-fill my profile</>}
        </Button>
        <div className="text-[11px] text-stone-500 text-center">Only the last 4 chars of your KYC number are stored. Photo and full documents are never shared publicly.</div>
      </CardContent></Card>
    </div>
  );
}


function ReviewCard({ extracted, onChange, onConfirm, busy, onBack }) {
  const [edited, setEdited] = useState({ ...extracted });
  const set = (k, v) => { const n = { ...edited, [k]: v }; setEdited(n); onChange(n); };
  const FIELDS = [
    ["full_name", "Full name"], ["dob", "DOB"], ["gender", "Gender"],
    ["address", "Address"], ["city", "City"], ["state", "State"], ["country_code", "Country"],
    ["business_name", "Business name"], ["designation", "Designation"], ["website", "Website"],
    ["office_phone", "Office phone"], ["office_email", "Office email"], ["linkedin", "LinkedIn"],
    ["profession", "Profession"],
  ];
  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6" data-testid="onb-review">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="onb-review-back"><ArrowLeft size={16} className="mr-1" /> Back</Button>
        <h1 className="heading text-2xl font-extrabold flex items-center gap-2"><CheckCircle2 className="text-emerald-600" /> Review your details</h1>
      </div>
      <Card><CardHeader className="pb-2"><CardTitle className="text-base">Extracted from your documents — edit anything before saving</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {FIELDS.map(([k, label]) => (
              <Field key={k} label={label}>
                <Input value={edited[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`onb-field-${k}`} />
              </Field>
            ))}
          </div>
          {edited.notes && <div className="text-xs text-stone-500 italic border-l-2 border-stone-200 pl-3">{edited.notes}</div>}
          <Button disabled={busy} onClick={onConfirm} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] gap-2" data-testid="onb-confirm">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><CheckCircle2 size={14} /> Save to my profile</>}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}


function UploadTile({ label, icon, value, onFile, testId, optional = false }) {
  const inputRef = useRef();
  return (
    <div className="flex items-center gap-4">
      <div className="w-20 h-20 rounded-xl bg-stone-100 border-2 border-dashed border-stone-300 overflow-hidden flex items-center justify-center">
        {value ? <img loading="lazy" src={value} alt="" className="w-full h-full object-cover" /> : <div className="text-stone-400">{icon}</div>}
      </div>
      <div>
        <div className="text-sm font-semibold">{label} {optional && <span className="text-xs text-stone-400 font-normal">(optional)</span>}</div>
        <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} className="rounded-full mt-1 gap-1" data-testid={`${testId}-btn`}>
          <Camera size={12} /> {value ? "Replace" : "Add"}
        </Button>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid={testId} />
      </div>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-widest text-stone-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
